/**
 * Shared helpers for Purchase Order General Info related parties (vendor, ship-to).
 */

import { readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { getNodelistNodeTitle, getNodelistPrimaryKey } from 'utilities/NodelistField';
import { formatLocationPostalValue } from 'utilities/Location';
import { formatPhoneValueForDisplay } from 'utilities/Phone';

function normalizeString(value) {
  return String(value || '').trim();
}

function formatPartyPhone(value) {
  return formatPhoneValueForDisplay(value) || normalizeString(value);
}

/**
 * @param {object|null|undefined} nodeType
 * @param {string} fieldName
 * @returns {object|null}
 */
export function getPurchaseOrderSchemaField(nodeType, fieldName) {
  const name = normalizeString(fieldName);
  const fields = Array.isArray(nodeType?.schema?.fields) ? nodeType.schema.fields : [];
  return fields.find((field) => normalizeString(field?.name) === name) || null;
}

/**
 * @param {object|null|undefined} relatedNode
 * @param {string} fieldName
 * @param {string} [typeKey='VENDOR']
 * @returns {unknown}
 */
export function readRelatedPartyField(relatedNode, fieldName, typeKey = 'VENDOR') {
  if (!relatedNode || !fieldName) return undefined;
  return readNodeFieldRawValue(
    relatedNode,
    { name: fieldName, source: String(typeKey).toLowerCase() },
    typeKey,
  );
}

/**
 * @param {object|null|undefined} vendorNode
 * @returns {{
 *   title: string,
 *   address: string,
 *   phone: string,
 *   email: string,
 * }}
 */
export function getVendorPartyDisplay(vendorNode) {
  if (!vendorNode) {
    return {
      title: '',
      address: '',
      phone: '',
      email: '',
    };
  }

  return {
    title: getNodelistNodeTitle(vendorNode),
    address: formatLocationPostalValue(readRelatedPartyField(vendorNode, 'address', 'VENDOR')) || '',
    phone: formatPartyPhone(readRelatedPartyField(vendorNode, 'phoneNumber', 'VENDOR')),
    email: normalizeString(readRelatedPartyField(vendorNode, 'emailAddress', 'VENDOR')),
  };
}

/**
 * @param {object|null|undefined} purchaseOrderNode
 * @param {object|null|undefined} nodeType
 * @returns {string}
 */
export function getPurchaseOrderVendorKey(purchaseOrderNode, nodeType = null) {
  const typeKey = normalizeString(nodeType?.key || purchaseOrderNode?.type).toUpperCase() || 'PURCHASE_ORDER';
  const raw = readNodeFieldRawValue(
    purchaseOrderNode,
    { name: 'vendor', source: typeKey.toLowerCase() },
    typeKey,
  );
  return getNodelistPrimaryKey(raw);
}

/**
 * @param {object|null|undefined} contactNode
 * @returns {{
 *   title: string,
 *   address: string,
 *   phone: string,
 *   email: string,
 * }}
 */
export function getShipToPartyDisplay(contactNode) {
  if (!contactNode) {
    return {
      title: '',
      address: '',
      phone: '',
      email: '',
    };
  }

  return {
    title: getNodelistNodeTitle(contactNode),
    address: formatLocationPostalValue(
      readRelatedPartyField(contactNode, 'primaryAddress', 'CONTACT'),
    ) || '',
    phone: formatPartyPhone(readRelatedPartyField(contactNode, 'primaryPhone', 'CONTACT')),
    email: normalizeString(readRelatedPartyField(contactNode, 'primaryEmail', 'CONTACT')),
  };
}

/**
 * @param {object|null|undefined} purchaseOrderNode
 * @param {object|null|undefined} nodeType
 * @returns {string}
 */
export function getPurchaseOrderShipToKey(purchaseOrderNode, nodeType = null) {
  const typeKey = normalizeString(nodeType?.key || purchaseOrderNode?.type).toUpperCase() || 'PURCHASE_ORDER';
  const raw = readNodeFieldRawValue(
    purchaseOrderNode,
    { name: 'shipTo', source: typeKey.toLowerCase() },
    typeKey,
  );
  return getNodelistPrimaryKey(raw);
}
